// Shared constants/helpers for the Audit Logs screen (GET /admin/audit-logs,
// super-admin only). `log_name` is explicitly NOT an enforced enum per the
// docs — new categories can ship without a redeploy — so this baseline list
// is a starting point for the filter dropdown, not a whitelist. The screen
// merges these with whatever `log_name` values actually show up in fetched
// data (see mergeKnownValues below), so an unrecognized category still gets
// a sensible fallback badge instead of being silently excluded.

// "support_ticket" was this file's original guess at the category name;
// live data confirmed the real value is "support" — removed the duplicate
// rather than keep both showing up in the dropdown.
export const KNOWN_LOG_NAMES = ['investment_plan', 'admin_action', 'kyc', 'account_security', 'support'];

// label + Badge tone per known log_name. Anything not listed here falls back
// to a title-cased version of the raw value with a neutral tone (see
// logNameMeta below) rather than crashing or showing nothing.
const LOG_NAME_META = {
  investment_plan: { label: 'Investment Plan', tone: 'info' },
  admin_action: { label: 'Admin Action', tone: 'neutral' },
  kyc: { label: 'KYC', tone: 'success' },
  account_security: { label: 'Account Security', tone: 'danger' },
  support: { label: 'Support', tone: 'warning' },
};

function titleCase(value) {
  return String(value ?? '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function logNameMeta(logName) {
  if (!logName) return { label: 'Unknown', tone: 'neutral' };
  return LOG_NAME_META[logName] || { label: titleCase(logName), tone: 'neutral' };
}

// Merges a baseline list with whatever values are actually present in the
// current page of data, so the filter dropdown always includes every known
// category plus anything new the backend has started sending — without
// ever needing a hardcoded enum update to "discover" it.
export function mergeKnownValues(baseline, observed) {
  const set = new Set(baseline);
  observed.forEach((v) => {
    if (v) set.add(v);
  });
  return Array.from(set);
}

// `to` earlier than `from` is a 422 server-side — this just saves an
// obviously-doomed round trip, same reasoning as reportSchema's
// validateReportDateRange.
export function validateAuditDateRange(from, to) {
  if (!from || !to) return null;
  if (new Date(to) < new Date(from)) return '"To" date must be on or after "From" date.';
  return null;
}

// Best-effort prettification for the machine-readable `description` key
// (confirmed example: "admin_makeAdmin"). Only the one example is
// confirmed against live data — the rest below are reasonable guesses at
// likely action keys given this app's own admin/KYC/ticket actions. That's
// safe: an unmapped key just falls through to the raw string (per the spec's
// explicit instruction not to hide or break on unrecognized values), so a
// wrong guess here costs nothing.
const DESCRIPTION_MAP = {
  admin_makeAdmin: 'Admin access granted',
  admin_revokeAdmin: 'Admin access revoked',
  admin_deleteAdmin: 'Admin account deleted',
  admin_createAdmin: 'Admin account created',
  admin_updateAdmin: 'Admin profile updated',
  admin_unlockAccount: 'Account unlocked',
  kyc_approved: 'KYC submission approved',
  kyc_rejected: 'KYC submission rejected',
  kyc_submitted: 'KYC submitted for review',
  investment_plan_created: 'Investment plan created',
  investment_plan_updated: 'Investment plan updated',
  investment_plan_deleted: 'Investment plan deleted',
  support_resolved: 'Support ticket resolved',
  support_closed: 'Support ticket closed',
  account_security_login_failed: 'Failed login attempt',
  account_security_locked: 'Account locked after repeated failed logins',
};

export function prettifyDescription(description) {
  return DESCRIPTION_MAP[description] || description || '—';
}
